// DrinkTile — ONE TAP = ONE DRINK LOGGED.
// Ported from apps/web/src/design-canon/project/src/screens-logger.jsx → DrinkTile,
// SIMPLIFIED: no glass tip, no spill puddle, no drops, no refill animation.
// The full multi-phase drain/spill turned out to read as "spilling" rather than
// "drinking" — the user wants the tile to behave like a button, not a vignette.
//
// State machine (simplified):
//   idle    (glass icon shown, count visible if > 0)
//     ↓ tap
//   logging (200ms scale(0.95) press + check-mark badge for ~800ms)
//     ↓
//   idle    (back to ready, count incremented)
//
// onLog fires synchronously on tap. Optimistic count bump lives in the parent
// route (see routes/night/$id/index.tsx), which also dispatches the
// `stumble:drink-logged` CustomEvent the SelfieSheet listens for.
//
// Reduced motion: skip the scale-pop and just show the check-mark briefly.
//
// Canon screen reference: design-canon/project/src/screens-logger.jsx lines 97-168.

import type { ReactElement } from 'react'
import { useEffect, useRef, useState } from 'react'

export type DrinkType = 'beer' | 'wine' | 'cocktail' | 'shot'

type GlassDef = {
  /** Liquid color (full glass — no animation on the level). */
  color: string
  /** Foam highlight at the top of the liquid. */
  foam: string
  viewBox: string
  /** Top-Y of the interior (where liquid surface sits when full). */
  iTop: number
  /** Bottom-Y of the interior (where liquid bottom sits). */
  iBot: number
  /** SVG path describing the interior fill region (clip). */
  interior: string
  /** Stroke outline group rendered over the liquid. */
  outline: ReactElement
}

const GLASSES: Record<DrinkType, GlassDef> = {
  beer: {
    color: 'var(--orange)',
    foam: 'rgba(255,250,240,0.92)',
    viewBox: '0 0 100 140',
    iTop: 30,
    iBot: 130,
    interior: 'M24 30 L24 128 Q24 132 28 132 L72 132 Q76 132 76 128 L76 30 Z',
    outline: (
      <g
        fill="none"
        stroke="var(--line)"
        strokeWidth={3.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M22 30 L22 130 Q22 134 27 134 L73 134 Q78 134 78 130 L78 30" />
        <path d="M22 30 Q50 25 78 30" />
        <path d="M78 50 Q92 50 92 70 L92 92 Q92 108 78 108" />
      </g>
    ),
  },
  wine: {
    color: 'var(--pink)',
    foam: 'rgba(255,250,240,0.35)',
    viewBox: '0 0 100 180',
    iTop: 32,
    iBot: 80,
    interior: 'M27 32 Q27 78 50 80 Q73 78 73 32 Q50 28 27 32 Z',
    outline: (
      <g
        fill="none"
        stroke="var(--line)"
        strokeWidth={3.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M25 30 Q25 80 50 82 Q75 80 75 30 Q50 26 25 30 Z" />
        <line x1={50} y1={82} x2={50} y2={148} />
        <line x1={28} y1={150} x2={72} y2={150} />
      </g>
    ),
  },
  cocktail: {
    color: 'var(--teal)',
    foam: 'rgba(255,250,240,0.35)',
    viewBox: '0 0 100 180',
    iTop: 30,
    iBot: 84,
    interior: 'M17 30 L50 84 L83 30 Q50 26 17 30 Z',
    outline: (
      <g
        fill="none"
        stroke="var(--line)"
        strokeWidth={3.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M15 28 L50 86 L85 28 Q50 24 15 28 Z" />
        <line x1={50} y1={86} x2={50} y2={148} />
        <line x1={28} y1={150} x2={72} y2={150} />
        <circle cx={58} cy={62} r={5} fill="var(--lime)" stroke="var(--line)" strokeWidth={2} />
        <line x1={58} y1={62} x2={78} y2={34} stroke="var(--line)" strokeWidth={2} />
      </g>
    ),
  },
  // SHORT + STUBBY shot glass with a heavy base — should read "shot"
  // Canon (lines 58-76): outward-tapered cylinder, rim arc, heavy base.
  shot: {
    color: 'var(--acid)',
    foam: 'rgba(255,250,240,0.35)',
    viewBox: '0 0 100 90',
    iTop: 22,
    iBot: 70,
    interior: 'M24 22 L20 68 Q20 72 24 72 L76 72 Q80 72 80 68 L76 22 Z',
    outline: (
      <g
        fill="none"
        stroke="var(--line)"
        strokeWidth={4}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* glass walls */}
        <path d="M22 22 L18 70 Q18 74 22 74 L78 74 Q82 74 82 70 L78 22 Q50 17 22 22 Z" />
        {/* rim */}
        <path d="M22 22 Q50 17 78 22" />
        {/* heavy base */}
        <path d="M14 78 L86 78" strokeWidth={6} />
      </g>
    ),
  },
}

const TILE_BG: Record<DrinkType, string> = {
  beer: 'var(--lime)',
  wine: 'var(--pink)',
  cocktail: 'var(--orange)',
  shot: 'var(--acid)',
}

const TILE_FG: Record<DrinkType, string> = {
  beer: 'var(--ink)',
  wine: 'var(--paper)',
  cocktail: 'var(--paper)',
  shot: 'var(--ink)',
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export type DrinkTileProps = {
  type: DrinkType
  count: number
  /** Fired once per tap. May be async; we don't await it. */
  onLog: () => void | Promise<void>
}

// Animation timings (ms).
const POP_MS = 200
const CHECK_HOLD_MS = 800

export function DrinkTile({ type, count, onLog }: DrinkTileProps): ReactElement {
  const [pop, setPop] = useState(false)
  const [showCheck, setShowCheck] = useState(false)
  const timers = useRef<ReturnType<typeof setTimeout>[]>([])

  useEffect(() => {
    return () => {
      for (const t of timers.current) clearTimeout(t)
      timers.current = []
    }
  }, [])

  function tap() {
    // Fire the log immediately — one tap = one drink, no debounce here.
    // The parent route already debounces (250ms) and bumps optimistic count.
    void onLog()

    const reduce = prefersReducedMotion()
    if (!reduce) {
      setPop(true)
      const tPop = setTimeout(() => setPop(false), POP_MS)
      timers.current.push(tPop)
    }

    setShowCheck(true)
    const tCheck = setTimeout(() => setShowCheck(false), CHECK_HOLD_MS)
    timers.current.push(tCheck)
  }

  const g = GLASSES[type]
  const range = g.iBot - g.iTop
  const clipId = `drink-tile-${type}-clip`
  const countLabel = count > 0 ? `× ${count}` : 'tap to drink'

  return (
    <button
      type="button"
      className="drink-tile"
      onClick={tap}
      data-testid={`drink-${type}`}
      data-phase={showCheck ? 'logging' : 'idle'}
      aria-label={`Log a ${type}. Currently ${count}.`}
      style={{
        background: TILE_BG[type],
        color: TILE_FG[type],
        transform: pop ? 'scale(0.95)' : 'scale(1)',
        transition: `transform ${POP_MS}ms cubic-bezier(.3,1.3,.4,1)`,
      }}
    >
      <div className="drink-tile-glass">
        <svg viewBox={g.viewBox} preserveAspectRatio="xMidYMax meet" aria-hidden="true">
          <title>{type} glass</title>
          <defs>
            <clipPath id={clipId}>
              <path d={g.interior} />
            </clipPath>
          </defs>
          <g clipPath={`url(#${clipId})`}>
            <rect x={0} y={g.iTop} width={100} height={range} fill={g.color} />
            <rect x={0} y={g.iTop - 2} width={100} height={4} fill={g.foam} />
          </g>
          {g.outline}
        </svg>
      </div>

      <div className="drink-tile-meta">
        <div className="drink-tile-name">{type}</div>
        <div className="drink-tile-count">{countLabel}</div>
      </div>

      {showCheck && (
        <span className="drink-tile-recent" aria-hidden data-testid={`drink-${type}-check`}>
          <svg
            viewBox="0 0 24 24"
            width={14}
            height={14}
            fill="none"
            stroke="currentColor"
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <title>Recently added</title>
            <path d="M5 12 L10 17 L19 7" />
          </svg>
          +1
        </span>
      )}
    </button>
  )
}

/** Tiny outline-only glass icon used in the empties strip. */
export function MiniGlass({ type, size = 22 }: { type: DrinkType; size?: number }): ReactElement {
  const g = GLASSES[type]
  const [, , vw, vh] = g.viewBox.split(' ').map(Number)
  // Defensive — TS / runtime: fall back to 1:1 if parse fails.
  const aspect = vh && vw ? vh / vw : 1
  // Thicker stroke for the mini variant; clone outline so we don't mutate.
  // g.outline is a <g> ReactElement — its children are the path/line nodes.
  const outlineProps = g.outline.props as { children?: React.ReactNode }
  const thicker = (
    <g
      fill="none"
      stroke="var(--line)"
      strokeWidth={6}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {outlineProps.children}
    </g>
  )
  return (
    <svg
      viewBox={g.viewBox}
      width={size}
      height={size * aspect}
      style={{ display: 'block', overflow: 'visible' }}
      aria-hidden="true"
    >
      <title>{type} empty</title>
      {thicker}
    </svg>
  )
}
